/**
 * Builds one `ArTableExportSnapshot` — the per-sheet input `createXlsxWorkbookBlob`
 * turns into a worksheet — by reading a table's schema and rows straight from
 * Firestore rather than from the mounted grid's store.
 *
 * Consumed by `useFormXlsxExport` (the form and email/workflow XLSX), once per
 * table discovered in the form layout.
 *
 * Column visibility is resolved here, but bundle row, member-cell and
 * header-cell visibility is delegated to `resolveArTableBundleExportVisibility`,
 * shared with `useArTableExport`, so both XLSX producers agree with each other
 * and with the interactive grid. The resolver runs on the split-filtered rows
 * widened by `admitBundleHeaders`, and the ticked selection is intersected into
 * its output afterwards: a split that drops a header keeps it while a member
 * survives (SYD-2406), an unticked header must still orphan its member so the
 * export fails closed (SYD-2279).
 * The PDF instead classifies the selection subset and so withholds those members.
 *
 * Formula columns are not read from Firestore: the snapshot carries only
 * `formulaConfig`, and `buildArTableSheetSchema` evaluates each row, so a row whose
 * `calcInput` was never written or has since gone stale still exports right (SYD-2292).
 */

import { firestore } from '@/services/startFirestore';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import type {
  ArRowData,
  ArRowId,
  ArTableType,
  KitRowColumnCalc,
  TypeId,
} from '#types/System.Artifacts.types';
import { arTableRowsPath } from '#types/System.Artifacts.types';
import { createArFormulaEngineConfig } from '#types';
import { UIElementType } from '#types/System.types';
import resolveArColumnLabel from '@/components/ArTable/column/resolveArColumnLabel';
import type { FieldType, SplitQuery, UIArTable, VisibilityType, VisibilityTypeId } from '#types/System.types';
import type { DocEntry } from '@/store/Store.types';
import type { ArTableExportSnapshot } from './createXlsxWorkbookBlob';
import { isVisibleInVisibilityMode } from '@/components/rendering/isVisibleInVisibilityMode';
import { admitBundleHeaders } from '@/components/ArTable/bundleDisplay';
import { applyColumnConfig } from '@/components/ArTable/column/applyColumnConfig';
import { buildCompositeValueFormatter } from '@/components/ArTable/export/buildCompositeValueFormatter';
import { filterArRowIdsBySelection } from '@/components/ArTable/export/filterArRowIdsBySelection';
import { resolveSplitForTable } from '@/components/ArTable/split/resolveSplitForTable';
import { filterArRowIdsBySplit } from '@/components/ArTable/split/filterArRowIdsBySplit';
import { resolveArTableBundleExportVisibility } from '@/components/ArTable/export/resolveArTableBundleExportVisibility';
import { createBundleHeaderCellReader } from '@/components/ArTable/export/createBundleHeaderCellReader';
import { resolveKitRowColumnCalc } from '@/store/arTableKitRowCalc';

async function createArTableExportSnapshot(params: {
  formId: string;
  versionId: string;
  table: UIArTable;
  visibilityMode?: VisibilityTypeId;
  visibilityType?: VisibilityType;
  fieldTypes?: Record<string, FieldType>;
  splitQuery?: SplitQuery;
  selectedRowIds?: readonly ArRowId[] | null;
  /**
   * The form type's kit-row scaling rules. Merged under the table's own rules by
   * `resolveKitRowColumnCalc`, exactly as `ArTableBody` does — the sheet and the
   * grid must agree on which kit-header columns carry a driver cell.
   */
  formKitRowColumnCalc?: ReadonlyArray<KitRowColumnCalc>;
}): Promise<ArTableExportSnapshot> {
  const schemaRef = doc(firestore, `tableTypes/${params.table.tableId}`);
  const schemaSnap = await getDoc(schemaRef);
  const schema = schemaSnap.data() as ArTableType | undefined;
  if (!schema) {
    throw new Error(`Table schema not found: ${params.table.tableId}`);
  }

  const configuredColumns = params.table.columnConfig
    ? applyColumnConfig(schema.columns, params.table.columnConfig)
    : schema.columns;

  const columns = configuredColumns.filter((col) => {
      const visibilityMode = params.visibilityMode;

      if (visibilityMode === undefined || visibilityMode === 'internal') return true;

      if (Array.isArray(col.visibilityTypes) && col.visibilityTypes.includes('hidden')) return false;

      if (!isVisibleInVisibilityMode({ visibilityMode, visibilityTypes: col.visibilityTypes })) return false;

      if (col.uiElementType === UIElementType.Field) {
        const ft = params.fieldTypes?.[col.typeId];
        if (!isVisibleInVisibilityMode({ visibilityMode, visibilityTypes: ft?.visibilityTypes })) return false;
      }

      return true;
    });

  const rowsPath = arTableRowsPath(`forms/${params.formId}/formVersions/${params.versionId}`, params.table.tableId);
  const rowsRef = collection(firestore, rowsPath);
  const q = query(rowsRef, orderBy('order', 'asc'));
  const rowsSnap = await getDocs(q);

  const rowsMap = new Map<ArRowId, DocEntry<ArRowData>>();
  const rowIds: ArRowId[] = [];
  rowsSnap.forEach((d) => {
    const id = d.id as ArRowId;
    rowIds.push(id);
    rowsMap.set(id, { data: d.data() as ArRowData });
  });

  const formatCompositeValue = await buildCompositeValueFormatter(columns, params.fieldTypes);

  const tableLabel = schema.description?.shortLabel ?? params.table.tableId;

  /**
   * Over `schema.columns`, never the narrowed set: `applyColumnConfig` drops a
   * schema column the layout's `columnConfig` omits and the send view's
   * visibility narrows further, so a split column hidden in that view would make
   * the resolver return `null` — no filtering — and every recipient's sheet would
   * carry every recipient's rows. `resolveArTableVisibleRows` states the same
   * invariant for the grid; the sheet has to agree with it row for row.
   */
  const split = resolveSplitForTable({ columns: schema.columns, splitQuery: params.splitQuery });
  const rowIdsAfterSplit = filterArRowIdsBySplit({ rowIds, rowsDocs: rowsMap, split });

  /**
   * A header follows its members, exactly as `ArTableBody` and
   * `resolveArTableVisibleRows` do: a bundle keeping a member through the split
   * re-admits its header, so this sheet never carries a headerless member whose
   * bundle type is unresolvable and whose cells therefore fail closed
   * (SYD-2406). Gated on the two bundle flags for the same reason the grid gates
   * it: with bundles off, rows keep their persisted `bundleRole` but nothing
   * draws a bundle, so a re-admitted header would be a plain row the split
   * excluded.
   */
  const rowIdsWithBundleHeaders = admitBundleHeaders({
    rowIds,
    admittedRowIds: rowIdsAfterSplit,
    rowsDocs: schema.bundles?.kits?.enabled === true || schema.bundles?.groups === true
      ? rowsMap
      : null,
  });

  /**
   * Classify the split-narrowed rows, exactly as `ArTableBody` does, and only
   * then narrow to the ticked selection, which the grid likewise never
   * classifies against: an unticked header must keep orphaning its member so the
   * export fails closed (SYD-2279).
   */
  const bundleVisibility = resolveArTableBundleExportVisibility({
    rowIds: rowIdsWithBundleHeaders,
    rowsDocs: rowsMap,
    columns,
    visibilityMode: params.visibilityMode,
    visibilityType: params.visibilityType,
    kitsEnabled: schema.bundles?.kits?.enabled === true,
    groupsEnabled: schema.bundles?.groups === true,
  });

  const visibleRowIds = filterArRowIdsBySelection({
    rowIds: bundleVisibility.visibleRowIds,
    selectedRowIds: params.selectedRowIds ?? null,
  });

  /**
   * Through `resolveKitRowColumnCalc`, never `schema.kitRowColumnCalc` alone: it
   * merges the form type's rules and the global defaults in, as `ArTableBody`
   * does, so the sheet and the grid agree on which kit-header columns are drivers.
   */
  const kitCalcDriverTypeIds = new Set<TypeId>(
    resolveKitRowColumnCalc(schema.kitRowColumnCalc, params.formKitRowColumnCalc).map((calc) => calc.typeId),
  );

  return {
    tableId: params.table.tableId,
    filenameLabel: tableLabel,
    sheetName: tableLabel,
    columns,
    visibleRowIds,
    rowsMap,
    fieldTypes: params.fieldTypes,
    getColumnLabel: (column) => resolveArColumnLabel(column, params.fieldTypes ?? {}),
    isCellSuppressed: bundleVisibility.isCellSuppressed,
    /**
     * Header rows never fall through to the generic cell read, the formula overlay
     * included: the pre-SYD-2276 formula pass persisted zeros and dotted `{fields}`
     * objects onto them that no surface displays. The header map
     * comes from the resolver's single classification pass, the same one
     * `isCellSuppressed` uses, and is empty while both bundle flags are off — then
     * header rows export their own cells, as `ArTableBody`'s flat path renders them.
     */
    getBundleHeaderCell: createBundleHeaderCellReader({
      columns,
      rowsMap,
      bundleTypeByHeaderRowId: bundleVisibility.bundleTypeByHeaderRowId,
      kitCalcDriverTypeIds,
    }),
    formulaConfig: createArFormulaEngineConfig(schema),
    formatCompositeValue,
  };
}

export default createArTableExportSnapshot;

